import { Box, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useState } from "react";
import { useLocation, useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { chunkedUpload } from "../chunkedUploader";
import Loading from "../Loading";
import { buildBackTo } from "../routes/backLink";
import { getFileType } from "../utils";
import DocumentForm, { type IFormValues } from "./DocumentForm";
import {
    type DocumentEditMutation,
    type DocumentEditMutationVariables,
    type DocumentQuery,
    type DocumentQueryVariables,
    documentQuery,
    editMutation,
} from "./queries";

function Edit(): JSX.Element {
    const { documentId } = useParams();
    const { search } = useLocation();

    const backTo = buildBackTo(search);

    const navigate = useNavigate();
    const [uploading, setUploading] = useState(false);
    const [editResult, executeEdit] = useMutation<DocumentEditMutation, DocumentEditMutationVariables>(editMutation);
    const [result] = useQuery<DocumentQuery, DocumentQueryVariables>({
        query: documentQuery,
        variables: {
            documentId: Number.parseInt(documentId!, 10),
        },
    });

    const handleSubmit = async (values: IFormValues) => {
        let storedName = "";
        if (values.file) {
            try {
                setUploading(true);
                storedName = await chunkedUpload({ file: values.file! });
            } catch (error) {
                let message: string;
                if (error instanceof Error) {
                    message = error.message;
                } else {
                    message = "Unknown error";
                }
                notifications.show({
                    title: "Failed to upload file",
                    message,
                    color: "red",
                });
                return;
            } finally {
                setUploading(false);
            }
        }

        const result = await executeEdit({
            data: {
                id: Number.parseInt(documentId!, 10),
                documentType: values.documentType,
                title: values.title,
                author: values.author || null,
                publication: values.publication || null,
                tags: values.tags,
                reprocess: values.reprocess,
                content: values.reprocess ? values.content || null : null,
                url: values.reprocess ? values.url || null : null,
                fileData:
                    values.reprocess && values.file
                        ? { fileType: getFileType(values.file)!, fileName: values.file.name, file: storedName }
                        : null,
            },
        });
        if (result.error) {
            notifications.show({
                title: "Failed to save Document",
                message: result.error.message,
            });
        } else {
            const document = result.data?.documentEdit;
            notifications.show({
                title: "Document saved",
                message: `Saved Document ${document!.title}`,
            });
            // Return to previous page if present
            navigate(backTo, { replace: true });
        }
    };

    let main = null;
    if (result.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else {
        const document = result.data?.document;
        if (!document) {
            main = <Box>Document not found</Box>;
        } else {
            const initialValues = {
                documentType: document.documentType,
                title: document.title,
                author: document.author || "",
                publication: document.publication || "",
                content: document.content || "",
                url: document.url || "",
                file: null,
                tags: document.tags,
                reprocess: true,
            };
            main = (
                <DocumentForm
                    handleSubmit={handleSubmit}
                    initialValues={initialValues}
                    edit={true}
                    fetching={editResult.fetching || uploading}
                    filePlaceholder={document.file?.fileName}
                />
            );
        }
    }

    return (
        <>
            <Title order={2}>Edit Document</Title>
            {main}
            <Link to={backTo}>Back</Link>
        </>
    );
}

export default Edit;
